import { trigger, transition, style, animate } from '@angular/animations';
import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Inject,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import Hls from 'hls.js';
import { combineLatest, Subject, Subscription, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/modules/auth/shared/auth.service';
import { selectUserDetailsData } from 'src/app/store/user-details/user-details.selectors';
import { RouterFlowService } from '../../services/router-flow.service';
import { SliderService } from '../../services/slider.service';
import { SettingsService } from '../../services/settings.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { compactMeetingTime, compactReunions, isCatalogueSelectable, isMeetingFinished } from './reunion-streaming-view';
import { LivePlaybackRecovery } from './live-playback-recovery';
import { PlaybackTrace } from './playback-trace';
import { ReunionGuide, ReunionMeeting, ReunionSession, ReunionStreamingService, streamingRoleAllowed } from '../../services/reunion-streaming.service';
import { cappedAutoLevel, preferredStreamingHeight, StreamingMaxHeight } from '../../services/streaming-playback-policy';

@Component({
  selector: 'app-streaming-player',
  templateUrl: './streaming-player.component.html',
  styleUrls: ['./streaming-player.component.scss'],
  animations: [
    trigger('enterAnimation', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        style({ opacity: 1 }),
        animate('200ms', style({ opacity: 0 })),
      ]),
    ]),
  ],
})
export class StreamingPlayerComponent implements OnInit, OnDestroy {
  streamUrl =
    'https://genybet-tvcp1-p28-cdn.hexaglobe.net/slot03/llhls/playlist.m3u8';
  iframeUrl: SafeResourceUrl;
  channelEmbedType = 'live';

  @ViewChild('streamVideo') streamVideo: ElementRef<HTMLVideoElement>;
  @ViewChild('meetingRail') meetingRail: ElementRef<HTMLElement>;
  private hls: Hls;
  private hlsAttaching = false;
  private mediaRepairUsed = false;
  private nativeFallback = false;
  // PMU must not depend on the streaming gateway: once its filtered master
  // fails, play the direct CDN master until the viewer reopens or re-picks PMU.
  private directPmuFallback = false;
  private publicViaGateway = false;
  // The current source has produced media (loadeddata). Native HLS requests
  // the gateway master once, so a later native error cannot be the gateway's.
  private sourceLoaded = false;
  private debugNative = false;
  private originalRemotePlayback: boolean | undefined;
  private playbackEngine: 'hls' | 'native' | 'none' = 'none';
  private trace: PlaybackTrace;
  diagnosticsEnabled = false;
  diagnosticText = '';
  readonly streamingEnabled: boolean;
  canSeeReunions = false;
  guide: ReunionGuide | null = null;
  guideFailed = false;
  guideLoading = false;
  playbackLoading = false;
  playbackError = '';
  playbackRetrying = false;
  userPaused = false;
  autoplayBlocked = false;
  maxHeight: StreamingMaxHeight = 480;
  private recovery: LivePlaybackRecovery;
  private mutedInitially = false;
  private sessionPending = false;
  private renewalFailures = 0;
  // Retained after teardown for local diagnostics; never contains URLs/tokens.
  playbackDiagnostic: {
    source: string; type: string; detail: string; status: number;
    mediaCode: number; readyState: number; recoveryAttempt: number; reason: string;
  } | null = null;
  selectedId: string | null = null;
  now = Date.now();
  panelOpen = false;
  otherStreamsOpen = false;
  selectedLegacyUrl: string | null = null;
  private pinnedMeeting: ReunionMeeting | null = null;
  private scrollTimer: ReturnType<typeof setTimeout>;
  private manualSelection = false;
  private rememberedMeetingId: string | null = null;
  private pendingReopenId: string | null = null;
  private restoringSession = false;
  private session: ReunionSession | null = null;
  private pendingChannelId: string | null = null;
  private guideSubscription: Subscription;
  private clockSubscription: Subscription;
  private renewSubscription = new Subscription();
  private selectionGeneration = 0;
  private recoveryCount = 0;
  private clockOffset = 0;
  private publicStream = { url: this.streamUrl, type: 'live' };
  private viewerUid = '';

  channels;
  isBrowser = false;
  sliders: any = [];
  // What the carousel actually renders: `sliders` filtered by audience.
  visibleSliders: any[] = [];
  showCarousel = true;
  isHomepage: boolean;
  // Audience gate for slider rows.
  //
  // A row whose `type` reads 'currency:XXX' is shown ONLY to a logged-in user
  // whose account currency is XXX; every other row (including '' and the
  // legacy 'homepage') stays public exactly as before. The rule lives in the
  // `type` column rather than in code so opening a new market — or retiring
  // one — is a data edit, and it matches the currency-based rule the payment
  // module and the home announcement already use.
  //
  // Anonymous visitors are excluded deliberately: without an account there is
  // no honest way to know the market, and a payments banner shown to someone
  // who cannot deposit is worse than no banner. Targeting guests by IP is a
  // later, separate decision.
  private static readonly CURRENCY_TYPE_PREFIX = 'currency:';
  private isAuthenticated = false;
  private audienceCurrency = '';
  private destroy$ = new Subject<void>();

  // Channel carousel shown while streaming is disabled (unchanged from before).
  responsiveOptions = [
    {
      breakpoint: '1024px',
      numVisible: 5,
      numScroll: 1,
    },
    {
      breakpoint: '768px',
      numVisible: 3,
      numScroll: 2,
    },
  ];
  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private domSanitizer: DomSanitizer,
    public router: Router,
    private sliderService: SliderService,
    private routerFlowService: RouterFlowService,
    private settingServ: SettingsService,
    private authService: AuthService,
    private store: Store,
    private reunionStreaming: ReunionStreamingService
  ) {
    this.streamingEnabled = reunionStreaming.enabled;
    router.events.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      router.url === '/' || router.url === '/?siteAdmin=true'
        ? (this.isHomepage = true)
        : (this.isHomepage = false);
    });

    this.routerFlowService.getRedirectInfo().pipe(takeUntil(this.destroy$)).subscribe((isRedirected) => {
      if (isRedirected) {
        this.showCarousel = false;
        router.events.pipe(takeUntil(this.destroy$)).subscribe((val) => {
          if (val instanceof NavigationEnd) this.showCarousel = true;
        });
      }
    });
  }

  ngOnInit(): void {
    this.isBrowser = isPlatformBrowser(this.platformId);
    if (this.isBrowser) {
      const query = new URLSearchParams(window.location.search);
      this.diagnosticsEnabled = query.get('streamDebug') === '1';
      this.debugNative = this.diagnosticsEnabled && query.get('streamEngine') === 'native';
      if (this.diagnosticsEnabled) {
        this.trace = new PlaybackTrace(() => ({
          engine: this.playbackEngine,
          stream: this.selectedId ? 'meeting' : this.selectedLegacyUrl ? 'legacy' : 'pmu',
          maxHeight: this.maxHeight, level: this.hls ? this.hls.currentLevel : -1,
          wanted: !!this.recovery?.wantsPlayback,
        }));
      }
      this.maxHeight = preferredStreamingHeight(window);
      this.watchAudience();
      this.getSliders();
      this.getStreaming();
    }
  }

  ngOnDestroy(): void {
    this.panelOpen = false;
    this.pendingReopenId = null;
    this.rememberedMeetingId = null;
    this.destroyRecovery();
    clearTimeout(this.scrollTimer);
    this.destroy$.next();
    this.destroy$.complete();
    this.stopGuide();
    this.releaseSession();
    this.teardownPlayer();
    this.trace?.detach();
  }

  // Re-filter on login, logout and profile load, so a user who signs in sees
  // their market's slider without a reload — and a user who signs out stops
  // seeing it immediately.
  private watchAudience(): void {
    combineLatest([
      this.authService.authStatusSub,
      this.store.select(selectUserDetailsData),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([credential, details]: [any, any]) => {
        this.isAuthenticated = !!(credential && credential.uid);
        this.audienceCurrency = (
          (details &&
            (details.countryCurrency ||
              (details.country && details.country.currency))) ||
          ''
        )
          .toString()
          .toUpperCase();
        this.applyAudience();
        const uid = (credential && credential.uid) || '';
        const allowed = this.streamingEnabled && this.isAuthenticated &&
          !!details && details.fbId === uid && streamingRoleAllowed(details.role);
        if (this.viewerUid !== uid || this.canSeeReunions !== allowed) {
          this.viewerUid = uid;
          this.canSeeReunions = allowed;
          this.stopGuide();
          this.guide = null;
          this.pinnedMeeting = null;
          this.pendingReopenId = null;
          this.rememberedMeetingId = null;
          if (this.selectedId) {
            this.selectPublicStream(false);
          }
          if (allowed) {
            this.startGuide();
            this.startClock();
          }
        }
      });
  }

  private applyAudience(): void {
    this.visibleSliders = (this.sliders || []).filter((slider: any) =>
      this.isSliderForViewer(slider)
    );
  }

  private isSliderForViewer(slider: any): boolean {
    const type = ((slider && slider.type) || '').toString();
    const prefix = StreamingPlayerComponent.CURRENCY_TYPE_PREFIX;
    if (type.indexOf(prefix) !== 0) {
      return true; // untargeted row — public, unchanged behaviour
    }
    if (!this.isAuthenticated) {
      return false;
    }
    const wanted = type.slice(prefix.length).trim().toUpperCase();
    return !!wanted && wanted === this.audienceCurrency;
  }

  getSliders(): any {
    this.sliderService.getSlideInfo().pipe(takeUntil(this.destroy$)).subscribe((slide) => {
      const data = !slide.error ? slide.data : null
      data?.sort((a: { sort: number }, b: { sort: number }) => {
        if (a.sort > b.sort) {
          return 1;
        }
        if (a.sort < b.sort) {
          return -1;
        }
        return 0;
      });

      this.sliders = data || [];

      this.sliders.forEach((slider) => {
        if (
          this.settingServ.filterRaceCountries(slider?.page_url.split('/')[1])
        ) {
          slider.page_url = '';
        }
      });

      this.applyAudience();
    });
  }

  getStreaming() {
    this.sliderService.getStreamingList().pipe(takeUntil(this.destroy$)).subscribe((resp) => {
      this.channels = resp;
      if (this.channels?.length > 0) {
        this.publicStream = {
          url: this.channels[0]?.channelStreamSource,
          type: this.channels[0]?.channelEmbedType,
        };
        if (this.selectedId || this.manualSelection) {
          return;
        }
        // the DB channel list is the source of truth; the hardcoded
        // streamUrl is only a fallback while this request is in flight
        const previousUrl = this.streamUrl;
        const isPlaying =
          !!this.hls || !!this.streamVideo?.nativeElement?.currentSrc;
        this.setActiveChannel(
          this.channels[0]?.channelStreamSource,
          this.channels[0]?.channelEmbedType
        );
        if (
          isPlaying &&
          this.channelEmbedType !== 'iframe' &&
          this.streamUrl !== previousUrl
        ) {
          this.playStream();
        }
      }
    });
  }

  startVideo(url, channelEmbedType) {
    this.pendingReopenId = null;
    this.rememberedMeetingId = null;
    this.resetRecovery();
    this.manualSelection = true;
    this.selectedId = null;
    this.pinnedMeeting = null;
    this.selectedLegacyUrl = url === this.publicStream.url ? null : url;
    this.otherStreamsOpen = false;
    this.releaseSession();
    this.playbackError = '';
    this.playbackLoading = false;
    this.recoveryCount = 0;
    this.setActiveChannel(url, channelEmbedType);
    if (channelEmbedType !== 'iframe') {
      this.playStream();
    }
  }

  onTabClose(): any {
    this.trace?.record('close');
    this.panelOpen = false;
    this.pendingReopenId = null;
    this.destroyRecovery();
    clearTimeout(this.scrollTimer);
    this.showCarousel = true;
    // The guide watch stays up so the closed bar's dot still knows about races.
    this.stopClock();
    this.releaseSession();
    this.teardownPlayer();
    this.trace?.detach();
  }

  onTabOpen(): any {
    if (this.panelOpen) { return; }
    this.panelOpen = true;
    this.showCarousel = false;
    this.playbackError = '';
    this.recoveryCount = 0;
    this.directPmuFallback = false;
    this.now = Date.now() + this.clockOffset;
    if (this.streamingEnabled) {
      this.pendingReopenId = this.canSeeReunions ? this.rememberedMeetingId : null;
      // A closed panel's guide is not evidence that a meeting is still on air.
      // PMU remains usable while the first response from this open is checked.
      this.stopGuide();
      this.guide = null;
      this.guideFailed = false;
      this.selectPublicStream(false);
    } else {
      this.resetRecovery();
      if (this.channelEmbedType !== 'iframe') { this.playStream(); }
    }
    this.startGuide();
    this.startClock();
    this.scheduleSelectedChip();
  }

  selectPublicStream(manual = true): void {
    if (manual) {
      this.pendingReopenId = null;
      this.rememberedMeetingId = null;
      // An explicit PMU choice retries the gateway once; internal returns to
      // PMU keep the direct master so a dead gateway causes no repeated blips.
      this.directPmuFallback = false;
    }
    this.resetRecovery(!manual);
    this.manualSelection = manual;
    this.selectedId = null;
    this.pinnedMeeting = null;
    this.selectedLegacyUrl = null;
    this.otherStreamsOpen = false;
    this.scheduleSelectedChip();
    this.releaseSession();
    this.playbackLoading = false;
    this.playbackError = '';
    this.recoveryCount = 0;
    this.setActiveChannel(this.publicStream.url, this.publicStream.type);
    if (this.panelOpen && this.channelEmbedType !== 'iframe') {
      this.playStream();
    }
  }

  selectReunion(id: string, playable: boolean): void {
    if (!playable || !this.canSeeReunions || !this.panelOpen ||
      !this.eligibleMeeting(id) || (id === this.selectedId && !this.playbackError)) {
      return;
    }
    this.pendingReopenId = null;
    this.rememberedMeetingId = id;
    this.manualSelection = true;
    this.openSession(id);
  }

  retryPlayback(): void {
    this.pendingReopenId = null;
    if (!this.selectedId) { this.rememberedMeetingId = null; }
    this.resetRecovery();
    if (this.selectedId && this.canSeeReunions) {
      this.openSession(this.selectedId);
    } else {
      this.playbackError = '';
      this.recoveryCount = 0;
      this.playStream();
    }
  }

  retryGuide(): void {
    this.stopGuide();
    this.startGuide();
  }

  trackById(index: number, item: { id: string }): string {
    return item.id;
  }

  get selectedMeeting(): ReunionMeeting | null {
    return this.guide && this.guide.meetings.find((meeting) => meeting.id === this.selectedId) ||
      (this.pinnedMeeting && this.pinnedMeeting.id === this.selectedId ? this.pinnedMeeting : null);
  }

  get visibleMeetings(): ReunionMeeting[] {
    if (!this.canSeeReunions || !this.guide || !this.guide.enabled) { return []; }
    const meetings = this.guide.meetings.filter((meeting) => this.namedMeeting(meeting));
    if (this.selectedMeeting && !meetings.some((row) => row.id === this.selectedId)) {
      meetings.push({ ...this.selectedMeeting, playable: false });
    }
    return compactReunions(meetings, this.now, this.selectedId)
      .filter((meeting) => this.namedMeeting(meeting) && !isMeetingFinished(meeting, this.now) && isCatalogueSelectable(meeting, this.now))
      .map((meeting) => ({ ...meeting, label: (meeting.label || '').trim() || (meeting.trackName || '').trim() }));
  }

  /** The header dot beats while the rail offers at least one playable named meeting, closed or open. */
  get hasLiveMeetings(): boolean {
    return this.visibleMeetings.some((meeting) => meeting.playable);
  }

  get sharedSignalLabel(): string {
    const selected = this.visibleMeetings.find((meeting) => meeting.id === this.selectedId);
    return selected && selected.signalOwner && selected.signalOwner.id !== selected.id ? selected.signalOwner.label : '';
  }

  get legacyChannels(): any[] {
    if (this.streamingEnabled) { return []; }
    const seen = new Set([this.publicStream.url]);
    return (this.channels || []).filter((channel) => {
      const url = channel.channelStreamSource;
      if (!url || seen.has(url)) { return false; }
      seen.add(url);
      return true;
    });
  }

  get selectedLabel(): string {
    if (this.selectedMeeting) { return (this.selectedMeeting.label || '').trim() || (this.selectedMeeting.trackName || '').trim(); }
    const legacy = this.legacyChannels.find((row) => row.channelStreamSource === this.selectedLegacyUrl);
    return legacy && (legacy.channelName || legacy.name) || '';
  }

  meetingTime(meeting: ReunionMeeting): { key: string; minutes?: number; time?: string } {
    return compactMeetingTime(meeting, this.now);
  }

  meetingRace(meeting: ReunionMeeting): ReunionMeeting['race'] {
    return meeting.catalogueRace === undefined ? meeting.race : meeting.catalogueRace;
  }

  confirmedLive(meeting: ReunionMeeting): boolean {
    return this.meetingTime(meeting).key === 'STREAMING.LIVE';
  }

  private namedMeeting(meeting: ReunionMeeting): boolean {
    return !!((meeting.label || '').trim() || (meeting.trackName || '').trim());
  }

  private eligibleMeeting(id: string): ReunionMeeting | undefined {
    if (!this.guide || !this.guide.enabled) { return undefined; }
    // Eligibility deliberately omits the selected-row pin used for uninterrupted
    // playback. A reopen or a new session needs a currently visible meeting.
    return compactReunions(this.guide.meetings.filter((meeting) => this.namedMeeting(meeting)),
      Date.now() + this.clockOffset, null).find((meeting) => meeting.id === id &&
        isCatalogueSelectable(meeting, Date.now() + this.clockOffset));
  }

  private scheduleSelectedChip(): void {
    clearTimeout(this.scrollTimer);
    if (!this.isBrowser || !this.panelOpen) { return; }
    this.scrollTimer = setTimeout(() => {
      const rail = this.meetingRail && this.meetingRail.nativeElement;
      const selected = rail && rail.querySelector<HTMLElement>('.is-selected');
      if (!rail || !selected) { return; }
      const box = rail.getBoundingClientRect();
      const chip = selected.getBoundingClientRect();
      // Only move this rail horizontally. scrollIntoView can move the page.
      if (chip.left < box.left) { rail.scrollLeft -= box.left - chip.left + 6; }
      else if (chip.right > box.right) { rail.scrollLeft += chip.right - box.right + 6; }
    }, 0);
  }

  get selectedMissing(): boolean {
    return !!(this.selectedId && this.guide &&
      !this.guide.meetings.some((meeting) => meeting.id === this.selectedId));
  }

  // The guide is watched for any viewer who may see named meetings, panel open or
  // closed: the header dot beats on it. Opening re-subscribes for a fresh read.
  private startGuide(): void {
    if (!this.canSeeReunions || this.guideSubscription) {
      return;
    }
    this.guideLoading = !this.guide;
    this.guideSubscription = this.reunionStreaming.watchGuide().subscribe((update) => {
      if (update.failed) {
        if (this.pendingReopenId) { this.rememberedMeetingId = null; this.pendingReopenId = null; }
        this.guideFailed = true;
        this.guideLoading = false;
        return;
      }
      if (!update.guide) {
        return;
      }
      this.guide = update.guide;
      this.guideLoading = update.guide.loading;
      this.guideFailed = false;
      const serverTime = Date.parse(update.guide.serverTime);
      this.clockOffset = Number.isFinite(serverTime) ? serverTime - Date.now() : 0;
      this.now = Date.now() + this.clockOffset;
      const pinned = update.guide.meetings.find((row) => row.id === this.selectedId);
      if (pinned) { this.pinnedMeeting = pinned; }
      if (this.pendingReopenId) {
        const candidate = this.pendingReopenId;
        this.pendingReopenId = null;
        if (!update.guide.loading && !update.guide.stale && update.guide.enabled &&
          !this.userPaused && !this.autoplayBlocked && this.eligibleMeeting(candidate)) {
          this.openSession(candidate, false, true);
        } else { this.rememberedMeetingId = null; }
      }
      if (!update.guide.enabled) {
        this.rememberedMeetingId = null;
        if (this.selectedId) {
          this.selectPublicStream(false);
        }
        return;
      }
      if (this.selectedId && (this.session || this.playbackLoading)) {
        const meeting = update.guide.meetings.find((row) => row.id === this.selectedId);
        if (!meeting || !meeting.playable) {
          if (this.restoringSession) {
            this.rememberedMeetingId = null;
            this.selectPublicStream(false);
          } else {
            this.failPlayback();
            this.playbackError = 'STREAMING.UNAVAILABLE';
          }
        } else if (meeting && (this.session || this.pendingChannelId) &&
          meeting.channelId !== (this.session ? this.session.channelId : this.pendingChannelId)) {
          // A provider may reassign a meeting to a different signal mid-day.
          // Preserve the selected meeting, but replace its outdated capability.
          this.openSession(this.selectedId, true, this.restoringSession);
        }
      }
    });
  }

  // The rail's one-second clock runs only while the panel is open.
  private startClock(): void {
    if (!this.panelOpen || !this.canSeeReunions || this.clockSubscription) {
      return;
    }
    this.clockSubscription = timer(0, 1000).subscribe(() => this.now = Date.now() + this.clockOffset);
  }

  private stopClock(): void {
    if (this.clockSubscription) { this.clockSubscription.unsubscribe(); }
    this.clockSubscription = undefined;
  }

  private stopGuide(): void {
    if (this.guideSubscription) { this.guideSubscription.unsubscribe(); }
    this.guideSubscription = undefined;
    this.stopClock();
  }

  private openSession(id: string, preserveIntent = false, restoring = false): void {
    if (!preserveIntent) { this.resetRecovery(); }
    this.releaseSession();
    this.restoringSession = restoring;
    this.teardownPlayer();
    this.selectedId = id;
    this.selectedLegacyUrl = null;
    this.otherStreamsOpen = false;
    this.scheduleSelectedChip();
    this.channelEmbedType = 'live';
    if (!this.recovery) { this.resetRecovery(); }
    this.playbackLoading = true;
    this.playbackError = '';
    this.playbackDiagnostic = null;
    this.recoveryCount = 0;
    const generation = this.selectionGeneration;
    const meeting = this.guide && this.guide.meetings.find((row) => row.id === id);
    if (meeting) { this.pinnedMeeting = meeting; }
    this.pendingChannelId = meeting ? meeting.channelId : null;
    this.sessionPending = true;
    // Let this bounded request finish so a session minted after close/switch
    // can be revoked, rather than left behind on the gateway until expiry.
    this.reunionStreaming.createSession(id, this.maxHeight).subscribe((session) => {
      if (generation !== this.selectionGeneration || !this.panelOpen || !this.canSeeReunions) {
        this.reunionStreaming.revokeSession(session.sessionId).subscribe();
        return;
      }
      this.sessionPending = false;
      const currentMeeting = this.eligibleMeeting(id);
      if (session.selectionId !== id || session.maxHeight !== this.maxHeight || !currentMeeting ||
        currentMeeting.channelId !== session.channelId) {
        this.reunionStreaming.revokeSession(session.sessionId).subscribe();
        if (restoring) {
          this.rememberedMeetingId = null;
          this.selectPublicStream(false);
        } else { this.failPlayback(); }
        return;
      }
      this.pendingChannelId = null;
      this.restoringSession = false;
      this.session = session;
      this.renewalFailures = 0;
      this.streamUrl = session.url;
      this.playStream();
      this.scheduleRenewal(generation);
    }, (error) => {
      if (generation === this.selectionGeneration) {
        this.sessionPending = false;
        if (restoring) {
          this.rememberedMeetingId = null;
          this.selectPublicStream(false);
        } else if (this.isTransient(error)) { this.queueRecovery(error); }
        else { this.failPlayback(error); }
      }
    });
  }

  private scheduleRenewal(generation: number, retry = false): void {
    if (!this.session) { return; }
    const session = this.session;
    const beforeExpiry = Date.parse(session.expiresAt) - (Date.now() + this.clockOffset) - 15000;
    const delay = retry ? Math.max(1000, Math.min(30000, 2000 * Math.pow(2, Math.min(this.renewalFailures, 4)), beforeExpiry)) :
      Math.max(1000, Math.min(session.renewAfterSeconds * 1000, beforeExpiry));
    this.renewSubscription.add(timer(delay).subscribe(() => {
      this.renewSubscription.add(this.reunionStreaming.renewSession(session.sessionId).subscribe((renewed) => {
        if (generation !== this.selectionGeneration) { return; }
        // The gateway extends the same capability: no video source mutation,
        // which also keeps native Safari playback uninterrupted.
        if (renewed.sessionId !== session.sessionId || renewed.url !== session.url || renewed.maxHeight !== session.maxHeight) {
          this.failPlayback();
          return;
        }
        this.session = renewed;
        this.renewalFailures = 0;
        this.scheduleRenewal(generation);
      }, (error) => {
        if (generation !== this.selectionGeneration) { return; }
        if (this.isTransient(error) && Date.parse(session.expiresAt) > Date.now() + this.clockOffset + 15000) {
          this.renewalFailures++;
          this.scheduleRenewal(generation, true);
        } else if (error.status === 410 || this.isTransient(error)) {
          this.releaseSession();
          this.queueRecovery(error);
        } else {
          this.failPlayback(error);
        }
      }));
    }));
  }

  private releaseSession(): void {
    this.selectionGeneration++;
    this.restoringSession = false;
    this.pendingChannelId = null;
    this.sessionPending = false;
    this.renewSubscription.unsubscribe();
    this.renewSubscription = new Subscription();
    if (this.session) {
      this.reunionStreaming.revokeSession(this.session.sessionId).subscribe();
      this.session = null;
    }
  }

  private failPlayback(error?: any): void {
    if (this.recovery) { this.recovery.stop(); }
    if (error) {
      const code = error.error && error.error.error && error.error.error.code;
      this.recordPlaybackDiagnostic('session', 'requestError',
        /^STREAMING_[A-Z_]+$/.test(code || '') ? code : '', error.status);
    }
    this.playbackLoading = false;
    this.playbackError = error && (error.status === 401 || error.status === 403)
      ? 'STREAMING.ACCESS_ERROR' : 'STREAMING.PLAYBACK_ERROR';
    this.releaseSession();
    this.teardownPlayer();
  }

  onVideoReady(): void {
    this.playbackLoading = false;
    this.sourceLoaded = true;
  }

  copyPlaybackReport(textarea: HTMLTextAreaElement): void {
    if (!this.trace) { return; }
    this.diagnosticText = this.trace.report();
    // LAN HTTP on an iPhone may not offer the Clipboard API. Keep a selectable
    // text fallback, and never upload this report automatically.
    textarea.value = this.diagnosticText;
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(this.diagnosticText).catch(() => {});
    } else {
      try { document.execCommand('copy'); } catch (_) { /* Manual copy remains available. */ }
    }
  }

  onVideoError(): void {
    if (this.panelOpen && !this.hls && this.streamVideo?.nativeElement?.getAttribute('src')) {
      // No HTTP status here either: an error before the gateway's public
      // master has produced media moves PMU to the direct CDN master once.
      // After loadeddata only CDN variants and segments are fetched, so later
      // errors keep the capped gateway master and take the usual backoff.
      if (!this.selectedId && this.publicViaGateway && !this.directPmuFallback && !this.sourceLoaded) {
        this.useDirectPmu('mediaError');
        return;
      }
      this.recordPlaybackDiagnostic('native', 'mediaError', '');
      // Native HLS does not expose HTTP status: code 4 can also mean a failed
      // manifest request. Recheck/remint expired capabilities before reloading;
      // unsupported engines are handled by the feature probe in playStream.
      if (this.selectedId && this.session && Date.parse(this.session.expiresAt) <= Date.now() + this.clockOffset + 15000) {
        this.releaseSession();
      }
      this.queueRecovery();
    }
  }

  private setActiveChannel(url: string, channelEmbedType: string): void {
    this.channelEmbedType = channelEmbedType;
    if (channelEmbedType === 'iframe') {
      this.destroyRecovery();
      this.teardownPlayer();
      this.iframeUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(url);
    } else {
      this.streamUrl = url;
    }
  }

  togglePlaybackIntent(): void {
    this.pendingReopenId = null;
    if (!this.recovery) { this.resetRecovery(); }
    if (!this.recovery) { return; }
    if (this.userPaused || this.autoplayBlocked) { this.recovery.resume(); }
    else { this.recovery.pause(); }
  }

  private resetRecovery(preservePause = false): void {
    const wasPaused = preservePause && (this.userPaused || this.autoplayBlocked);
    this.destroyRecovery();
    this.nativeFallback = false;
    const video = this.streamVideo && this.streamVideo.nativeElement;
    if (!this.panelOpen || !video || this.channelEmbedType === 'iframe') { return; }
    this.recovery = new LivePlaybackRecovery(video, {
      state: (state) => {
        this.playbackRetrying = state.retrying;
        this.userPaused = state.userPaused;
        this.autoplayBlocked = state.autoplayBlocked;
        if (state.userPaused || state.autoplayBlocked) { this.pendingReopenId = null; }
        if (state.retrying) { this.playbackLoading = true; }
        else if (!this.sessionPending && video.readyState >= 2 && !video.paused) { this.playbackLoading = false; }
      },
      reconnect: () => this.reconnectPlayback(),
      healthy: () => { this.recoveryCount = 0; },
      diagnostic: (event) => this.trace?.record(event),
    });
    if (wasPaused) { this.recovery.pause(); }
  }

  private destroyRecovery(): void {
    if (this.recovery) { this.recovery.destroy(); this.recovery = undefined; }
    this.playbackRetrying = false; this.userPaused = false; this.autoplayBlocked = false;
  }

  private isTransient(error: any): boolean {
    return !error || !error.status || [408, 425, 429, 500, 502, 503, 504].includes(error.status);
  }

  private queueRecovery(error?: any): void {
    if (error) {
      const code = error.error && error.error.error && error.error.error.code;
      this.recordPlaybackDiagnostic('session', 'requestError', /^STREAMING_[A-Z_]+$/.test(code || '') ? code : '', error.status);
    }
    this.playbackError = '';
    if (this.recovery) { this.recovery.retry(); }
  }

  private reconnectPlayback(): void {
    if (!this.panelOpen || this.channelEmbedType === 'iframe' || !this.recovery || !this.recovery.wantsPlayback ||
      this.sessionPending) { return; }
    if (this.selectedId && (!this.session || Date.parse(this.session.expiresAt) <= Date.now() + this.clockOffset + 15000)) {
      this.openSession(this.selectedId, true, this.restoringSession);
    } else if (this.hlsAttaching && this.fallbackToNative()) {
      // A MediaSource that never opens is bounded by the existing watchdog.
      // It already waits for visible/online playback intent before reaching here.
      return;
    } else {
      // Rebuild from the master: startLoad alone cannot recover a failed initial
      // manifest, and native HLS needs a source reload after an ended live window.
      this.playStream();
    }
  }

  private playStream(): void {
    const video = this.streamVideo && this.streamVideo.nativeElement;
    if (!video || !this.panelOpen || this.channelEmbedType === 'iframe') { return; }
    this.trace?.attach(video);
    if (!this.recovery) { this.resetRecovery(); }
    this.teardownPlayer();
    this.sourceLoaded = false;
    // A fresh muted inline start is permitted by mobile autoplay policies.
    // Subsequent source switches preserve the viewer's chosen volume/mute state.
    if (!this.mutedInitially) { video.muted = true; this.mutedInitially = true; }
    const source = this.resolvePlaybackSource();
    if (!this.nativeFallback && !this.debugNative && Hls.isSupported()) {
      try {
        this.originalRemotePlayback = !!(video as HTMLVideoElement & { disableRemotePlayback?: boolean }).disableRemotePlayback;
        this.hlsAttaching = true;
        this.mediaRepairUsed = false;
        this.playbackEngine = 'hls';
        this.trace?.record('engine_hls');
        this.hls = new Hls({
          // Equidia publishes ordinary HLS. PMU's LL-HLS part hold-back must
          // remain provider-controlled, so omit the explicit segment count there.
          ...(this.selectedId ? { lowLatencyMode: false, liveSyncDurationCount: 4 } : { lowLatencyMode: true }),
          liveDurationInfinity: true,
          backBufferLength: 30,
          maxBufferLength: 30,
          maxMaxBufferLength: 60,
          startLevel: 0,
          // The size-capping controller writes autoLevelCapping periodically;
          // our fixed device ceiling must survive resize/fullscreen changes.
          capLevelToPlayerSize: false,
          abrEwmaDefaultEstimate: 300000,
        });
        const player = this.hls;
        player.on(Hls.Events.ERROR, (event, data) => {
          if (this.hls === player) { this.handleHlsError(data); }
        });
        player.on(Hls.Events.MEDIA_DETACHING, () => {
          if (this.hls === player) {
            this.hlsAttaching = true;
            if (this.recovery) { this.recovery.beginMediaChange(); }
          }
        });
        player.on(Hls.Events.MEDIA_ATTACHED, () => {
          if (this.hls === player) { this.hlsAttaching = false; this.trace?.record('media_attached'); }
        });
        player.on(Hls.Events.MANIFEST_PARSED, () => {
          if (this.hls === player) {
            player.autoLevelCapping = cappedAutoLevel(player.levels, this.maxHeight);
            if (this.recovery) { this.recovery.play(); }
          }
        });
        player.on(Hls.Events.LEVELS_UPDATED, () => {
          if (this.hls === player) { player.autoLevelCapping = cappedAutoLevel(player.levels, this.maxHeight); }
        });
        player.loadSource(source);
        if (this.hls === player) { player.attachMedia(video); }
      } catch (_) {
        // Some browsers advertise MediaSource but fail to create/attach it.
        // A fallback uses the same filtered master and preserves viewer intent.
        if (!this.fallbackToNative()) {
          this.recordPlaybackDiagnostic('browser', 'unsupported', 'hlsSetupFailed');
          this.failPlayback(); this.playbackError = 'STREAMING.BROWSER_UNSUPPORTED';
        }
        return;
      }
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      this.playbackEngine = 'native';
      this.trace?.record('engine_native');
      video.src = source;
      video.load();
    } else {
      this.recordPlaybackDiagnostic('browser', 'unsupported', 'noHlsPlayback');
      this.failPlayback(); this.playbackError = 'STREAMING.BROWSER_UNSUPPORTED'; return;
    }
    if (this.recovery) { this.recovery.play(); }
  }

  private handleHlsError(data: any): void {
    this.trace?.record(data.details, data.response && data.response.code);
    if ((data.details === Hls.ErrorDetails.ATTACH_MEDIA_ERROR ||
      (this.hlsAttaching && data.details === Hls.ErrorDetails.INTERNAL_EXCEPTION)) &&
      this.fallbackToNative()) { return; }
    if (!data.fatal) { return; }
    // The master is PMU's only gateway request (variants and segments come
    // from the CDN), so any status on it, 401/403/503 included, means the
    // gateway failed rather than the viewer's access.
    if (this.publicViaGateway && !this.selectedId &&
      (data.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR ||
        data.details === Hls.ErrorDetails.MANIFEST_LOAD_TIMEOUT ||
        data.details === Hls.ErrorDetails.MANIFEST_PARSING_ERROR)) {
      this.useDirectPmu(data.details, data.response && data.response.code);
      return;
    }
    this.recoveryCount++;
    this.recordPlaybackDiagnostic('hls', data.type, data.details,
      data.response && data.response.code, playlistParsingReason(data.error && data.error.message));
    if (data.details === Hls.ErrorDetails.MANIFEST_INCOMPATIBLE_CODECS_ERROR ||
      data.details === Hls.ErrorDetails.BUFFER_ADD_CODEC_ERROR ||
      data.details === Hls.ErrorDetails.BUFFER_INCOMPATIBLE_CODECS_ERROR) {
      if (this.fallbackToNative()) { return; }
      this.failPlayback(); this.playbackError = 'STREAMING.BROWSER_UNSUPPORTED'; return;
    }
    const status = data.response && data.response.code;
    if (status === 401 || status === 403) {
      this.failPlayback({ status }); return;
    }
    if (status === 410 && this.selectedId) { this.releaseSession(); }
    if (data.type === Hls.ErrorTypes.MEDIA_ERROR && this.hls && !this.mediaRepairUsed &&
      this.recovery?.wantsPlayback && document.visibilityState !== 'hidden') {
      this.mediaRepairUsed = true;
      this.trace?.record('media_repair');
      this.recovery.beginMediaChange();
      try { this.hls.recoverMediaError(); return; } catch (_) { /* Fall through to bounded retry. */ }
    }
    this.queueRecovery();
  }

  // Named tracks exist only behind the gateway. Public PMU prefers the
  // gateway's height-filtered master and, after it fails, the direct CDN
  // master. hls.js autoLevelCapping still applies there, but the server-side
  // filter (the only ceiling native HLS honours) is lost until the viewer
  // re-picks PMU or reopens the panel.
  private resolvePlaybackSource(): string {
    const gateway = this.selectedId || this.directPmuFallback ? null :
      this.reunionStreaming.publicPmuUrl(this.streamUrl, this.maxHeight);
    this.publicViaGateway = !!gateway;
    return gateway || this.streamUrl;
  }

  private useDirectPmu(detail: string, status?: number): void {
    this.directPmuFallback = true;
    this.recordPlaybackDiagnostic('pmu', 'gatewayFallback', detail, status);
    this.trace?.record('pmu_direct_fallback');
    this.playStream();
  }

  private fallbackToNative(): boolean {
    const video = this.streamVideo?.nativeElement;
    if (this.nativeFallback || !this.panelOpen || !video?.canPlayType('application/vnd.apple.mpegurl')) { return false; }
    this.nativeFallback = true;
    this.trace?.record('native_fallback');
    this.playStream();
    return true;
  }

  private recordPlaybackDiagnostic(source: string, type: string, detail: string, status = 0, reason = ''): void {
    const safeCode = (value: string) => /^[a-zA-Z0-9_]{1,64}$/.test(value || '') ? value : '';
    const video = this.streamVideo?.nativeElement;
    this.playbackDiagnostic = {
      source: safeCode(source), type: safeCode(type), detail: safeCode(detail),
      status: Number.isInteger(status) && status >= 0 && status <= 599 ? status : 0,
      mediaCode: video && video.error ? video.error.code : 0,
      readyState: video ? video.readyState : 0,
      recoveryAttempt: this.recoveryCount,
      reason: safeCode(reason),
    };
    this.trace?.record(safeCode(detail) || safeCode(type), this.playbackDiagnostic.status);
    // Fixed diagnostic fields only; never log token-bearing HLS error objects.
    // Do not log the hls.js error object: it can include signed media URLs.
    // With streaming disabled the console stays as quiet as before the feature
    // unless diagnostics were requested (?streamDebug=1).
    if ((this.streamingEnabled || this.diagnosticsEnabled) &&
      (this.recoveryCount <= 3 || this.recoveryCount % 10 === 0)) {
      console.warn('[streaming] Playback failed ' + JSON.stringify(this.playbackDiagnostic));
    }
  }

  private teardownPlayer(): void {
    if (this.recovery) { this.recovery.beginMediaChange(); }
    if (this.hls) {
      const player = this.hls;
      this.hls = undefined;
      player.destroy();
    }
    this.hlsAttaching = false;
    this.playbackEngine = 'none';
    const video = this.streamVideo?.nativeElement;
    if (video) {
      // Hls.js sets this for ManagedMediaSource but doesn't restore it on destroy.
      if (this.originalRemotePlayback !== undefined) {
        (video as HTMLVideoElement & { disableRemotePlayback?: boolean }).disableRemotePlayback = this.originalRemotePlayback;
        this.originalRemotePlayback = undefined;
      }
      video.pause();
      video.removeAttribute('src');
      video.load();
    }
  }
}


/** Map known hls.js parser messages to fixed codes; raw messages may contain URLs. */
export function playlistParsingReason(message: unknown): string {
  if (typeof message !== 'string') { return ''; }
  if (message.indexOf('media sequence mismatch ') === 0) { return 'MEDIA_SEQUENCE_MISMATCH'; }
  if (message.indexOf('discontinuity sequence mismatch ') === 0) { return 'DISCONTINUITY_SEQUENCE_MISMATCH'; }
  if (message === 'Missing format identifier #EXTM3U' || message === 'no EXTM3U delimiter') { return 'MISSING_EXTM3U'; }
  if (message === 'no levels found in manifest') { return 'NO_VARIANTS'; }
  if (message === 'Missing Target Duration') { return 'MISSING_TARGET_DURATION'; }
  if (message === 'No Segments found in Playlist') { return 'NO_SEGMENTS'; }
  if (/^#EXT-X-[A-Z0-9-]+ must not appear more than once /.test(message)) { return 'DUPLICATE_MEDIA_TAG'; }
  if (/^#EXT-X-[A-Z0-9-]+ must appear before the first Media Segment /.test(message)) { return 'MEDIA_TAG_AFTER_SEGMENT'; }
  if (message.indexOf('Missing preceding EXT-X-DEFINE tag for Variable Reference:') === 0) { return 'MISSING_VARIABLE'; }
  if (message.indexOf('EXT-X-DEFINE QUERYPARAM:') === 0) { return 'QUERY_VARIABLE'; }
  if (message.indexOf('EXT-X-DEFINE duplicate Variable Name declarations:') === 0) { return 'DUPLICATE_VARIABLE'; }
  if (message.indexOf('EXT-X-DEFINE IMPORT attribute not found in Multivariant Playlist:') === 0) { return 'MISSING_IMPORTED_VARIABLE'; }
  return '';
}
